import { History, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useImportTramsHistory } from "@/hooks/useInvoices";
import { useResolvedFeatureFlags } from "@/hooks/useResolvedFeatureFlags";

interface ImportTramsHistoryButtonProps {
  tramsAccountId?: number;
  disabled?: boolean;
}

export function ImportTramsHistoryButton({
  tramsAccountId,
  disabled = false,
}: ImportTramsHistoryButtonProps) {
  const { isEnabled, isLoading: flagsLoading } = useResolvedFeatureFlags();
  const importMutation = useImportTramsHistory();
  const { toast } = useToast();

  if (flagsLoading || !isEnabled("tramsHistoryImport")) {
    return null;
  }

  const handleImport = async () => {
    try {
      const result = await importMutation.mutateAsync({ tramsAccountId });
      const errorSuffix =
        result.errors.length > 0 ? ` ${result.errors.length} error(s) — see details.` : "";
      const refiledPart = result.refiled > 0 ? `, recovered ${result.refiled}` : "";

      toast({
        title: "TRAMS history import complete",
        description: `Imported ${result.imported}${refiledPart}, skipped ${result.skipped}.${errorSuffix}`,
        variant: result.errors.length > 0 ? "destructive" : "default",
      });

      if (result.errors.length > 0) {
        console.warn("[ImportTramsHistory]", result.errors);
      }
    } catch (error: unknown) {
      toast({
        title: "Import failed",
        description: error instanceof Error ? error.message : "Could not import TRAMS history.",
        variant: "destructive",
      });
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            onClick={handleImport}
            disabled={disabled || importMutation.isPending}
          >
            {importMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <History className="h-4 w-4 mr-2" />
            )}
            {importMutation.isPending ? "Importing…" : "Import History"}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">Imports the last 3 years of invoices.</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
